'use client'

import { useDebouncedCallback } from 'use-debounce'
import { Geo } from '@aws-amplify/geo'
import { uniqBy } from 'lodash'

export type PlaceOption = {
  label: string | undefined
  value: string | undefined
  location?: {
    lat: number
    lon: number
  }
}

type UsePlaceSearchOptions = {
  countries?: string[]
  maxResults?: number
  searchType?: 'city' | 'address'
}

export const usePlaceSearch = (options: UsePlaceSearchOptions = {}) => {
  const {
    countries = ['FRA', 'BEL', 'LUX', 'CHE', 'NLD'],
    maxResults = 5,
    searchType = 'city',
  } = options

  const searchPlaceByText = useDebouncedCallback(
    async (inputValue: string, callback: (options: PlaceOption[]) => void) => {
      try {
        const places = await Geo.searchByText(inputValue, {
          countries,
          maxResults,
        })

        const result = places
          .filter((place) =>
            searchType === 'city' ? place.municipality : true,
          )
          .map((place) => {
            const point = place?.geometry?.point
            const hasValidCoordinates =
              typeof point?.[0] === 'number' && typeof point?.[1] === 'number'

            return {
              label:
                searchType === 'city'
                  ? `${place.municipality}, ${place.postalCode}`
                  : place.label,
              value: searchType === 'city' ? place.municipality : place.label,
              ...(hasValidCoordinates
                ? {
                    location: {
                      lat: point[1],
                      lon: point[0],
                    },
                  }
                : {}),
            }
          })

        const deduplicatePlaces = uniqBy(result, 'value')
        callback(deduplicatePlaces)
      } catch (error) {
        console.error(error)
        callback([])
      }
    },
    300,
  )

  const loadOptions = (
    inputValue: string,
    callback: (options: PlaceOption[]) => void,
  ) => {
    return new Promise(() => searchPlaceByText(inputValue, callback))
  }

  return { loadOptions }
}
